import { useState, useMemo, Fragment, ReactNode } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  OnChangeFn,
  getExpandedRowModel,
  ExpandedState,
} from '@tanstack/react-table';
import { AnimatePresence } from 'framer-motion';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  AnimatedTableRow,
} from '@predoc/cow';
import { cn } from '@/lib/utils';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  emptyState?: ReactNode;
  accordionContent?: (row: TData) => React.ReactNode;
  grouping?: string[];
  /** Shows a loading state in the body while keeping the header mounted. */
  isLoading?: boolean;
  pageSize?: number;
}

type RowSpanInfo = { isFirst: boolean; span: number };

const BODY_ROW_CLASS = 'h-14';

const DataTable = <TData, TValue>({
  columns,
  data,
  sorting = [],
  onSortingChange,
  emptyState,
  accordionContent,
  grouping,
  isLoading = false,
  pageSize,
}: DataTableProps<TData, TValue>) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const groupColumnId = grouping?.[0];
  const isGrouped = !!groupColumnId;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      expanded,
    },
    onSortingChange: onSortingChange,
    onExpandedChange: setExpanded,
    getRowCanExpand: () => !!accordionContent,
    enableMultiSort: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
  });

  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();

  const rowSpanByRowId = useMemo(() => {
    if (!groupColumnId) return null;
    const info = new Map<string, RowSpanInfo>();
    let i = 0;
    while (i < rows.length) {
      const currentValue = String(rows[i].getValue(groupColumnId) ?? '');
      let j = i + 1;
      while (
        j < rows.length &&
        String(rows[j].getValue(groupColumnId) ?? '') === currentValue
      ) {
        j += 1;
      }
      const span = j - i;
      info.set(rows[i].id, { isFirst: true, span });
      for (let k = i + 1; k < j; k += 1) {
        info.set(rows[k].id, { isFirst: false, span: 0 });
      }
      i = j;
    }
    return info;
  }, [rows, groupColumnId]);

  const hasRows = rows.length > 0;
  const skeletonRowCount = pageSize ?? Math.min(Math.max(data.length, 3), 10);
  return (
    <div className="overflow-x-auto rounded-md border shadow-sm">
      <Table aria-busy={isLoading} className="table-fixed">
        <colgroup>
          {visibleColumns.map((column, index) => (
            <col
              key={`${column.id}-${index}`}
              style={{ width: `${column.getSize()}px` }}
            />
          ))}
        </colgroup>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta as
                  | { className?: string }
                  | undefined;
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'h-auto min-h-10 py-2 break-words whitespace-normal',
                      meta?.className,
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        {isLoading ? (
          <TableBody>
            {Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
              <TableRow key={`loading-${rowIndex}`} className={BODY_ROW_CLASS}>
                {visibleColumns.map((column) => {
                  const meta = column.columnDef.meta as
                    | { className?: string }
                    | undefined;

                  return (
                    <TableCell
                      key={`loading-${column.id}`}
                      className={cn(BODY_ROW_CLASS, meta?.className)}
                    >
                      <div className="animate-pulse">
                        <div className="bg-muted h-4 w-full rounded" />
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        ) : hasRows ? (
          <TableBody>
            {rows.map((row) => {
              const rowSpanInfo = rowSpanByRowId?.get(row.id);
              return (
                <Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    className={cn(
                      'group',
                      BODY_ROW_CLASS,
                      !row.getIsExpanded() || isGrouped ? 'bg-white' : '',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta as
                        | { className?: string }
                        | undefined;

                      if (
                        isGrouped &&
                        cell.column.id === groupColumnId &&
                        rowSpanInfo
                      ) {
                        if (!rowSpanInfo.isFirst) return null;
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              'align-top',
                              BODY_ROW_CLASS,
                              rowSpanInfo.span > 1 ? 'border-r' : '',
                              meta?.className,
                            )}
                            rowSpan={rowSpanInfo.span}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(BODY_ROW_CLASS, meta?.className)}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {!isGrouped && (
                    <AnimatePresence>
                      {row.getIsExpanded() && (
                        <AnimatedTableRow key={`${row.id}-expanded`}>
                          <TableCell colSpan={columns.length} className="p-0">
                            {accordionContent?.(row.original)}
                          </TableCell>
                        </AnimatedTableRow>
                      )}
                    </AnimatePresence>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        ) : emptyState ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} className="p-0">
                {emptyState}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    </div>
  );
};

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}
const TableSkeleton = ({
  rows = 5,
  columns = 6,
  showHeader = true,
  className = '',
}: TableSkeletonProps) => {
  return (
    <div className={`rounded-lg border bg-white shadow-sm ${className}`}>
      <Table>
        {showHeader && (
          <TableHeader>
            <TableRow>
              {Array.from({ length: columns }).map((_, index) => (
                <TableHead
                  key={index}
                  className="text-muted-foreground text-xs font-medium tracking-wider"
                >
                  <div className="animate-pulse">
                    <div className="bg-muted h-4 w-20 rounded"></div>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <TableCell key={colIndex}>
                  <div className="animate-pulse">
                    <div className="bg-muted h-4 w-full rounded"></div>
                  </div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export { DataTable, TableSkeleton };
